/**
 * Utilitários de segurança para o frontend
 */

// Configurações de CSP (Content Security Policy)
import { config } from '../config/env';

const DEV = process.env.NODE_ENV !== 'production';

const getApiOrigin = () => {
  try {
    if (!config?.API_BASE_URL) return null;
    const u = new URL(config.API_BASE_URL);
    return `${u.protocol}//${u.host}`;
  } catch {
    return null;
  }
};

export const CSP_CONFIG = (() => {
  const apiOrigin = getApiOrigin();
  if (DEV) {
    const connect = ["'self'", 'ws:', 'wss:'];
    if (apiOrigin) connect.push(apiOrigin);
    return {
      'default-src': ["'self'"],
      'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'"],
      'style-src': ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com'],
      'font-src': ["'self'", 'https://fonts.gstatic.com'],
      'img-src': ["'self'", 'data:', 'https:'],
      'connect-src': connect,
      'frame-ancestors': ["'none'"],
      'base-uri': ["'self'"],
      'form-action': ["'self'"],
    };
  }
  const connect = ["'self'"];
  if (apiOrigin) connect.push(apiOrigin);
  return {
    'default-src': ["'self'"],
    'script-src': ["'self'"],
    'style-src': ["'self'", 'https://fonts.googleapis.com'],
    'font-src': ["'self'", 'https://fonts.gstatic.com'],
    'img-src': ["'self'", 'data:', 'https:'],
    'connect-src': connect,
    'frame-ancestors': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
  };
})();

/**
 * Gera string CSP
 */
export const generateCSP = (config = CSP_CONFIG) => {
  return Object.entries(config)
    .map(([directive, sources]) => {
      if (sources.length === 0) {
        return directive;
      }
      return `${directive} ${sources.join(' ')}`;
    })
    .join('; ');
};

/**
 * Aplica CSP ao documento
 */
export const applyCSP = (config = CSP_CONFIG) => {
  if (typeof document === 'undefined') return;

  const cspString = generateCSP(config);

  // Remover CSP existente
  const existingMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (existingMeta) {
    existingMeta.remove();
  }

  // Adicionar novo CSP
  const meta = document.createElement('meta');
  meta.setAttribute('http-equiv', 'Content-Security-Policy');
  meta.setAttribute('content', cspString);
  document.head.appendChild(meta);
};

/**
 * Verifica se o ambiente é seguro (HTTPS)
 */
export const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

/**
 * Verifica se o domínio é confiável
 */
export const isTrustedDomain = (allowedDomains = []) => {
  const currentDomain = window.location.hostname;
  const defaults = ['localhost', '127.0.0.1', 'app.minasprojet.com.br'];
  return (
    allowedDomains.includes(currentDomain) ||
    defaults.includes(currentDomain) ||
    currentDomain.endsWith('.minasprojet.com.br')
  );
};

/**
 * Sanitiza URL para evitar redirects maliciosos
 */
export const sanitizeUrl = url => {
  if (!url) return null;

  try {
    const urlObj = new URL(url, window.location.origin);

    // Verificar se é do mesmo domínio ou domínios confiáveis
    const trustedDomains = [
      'localhost',
      '127.0.0.1',
      'app.minasprojet.com.br',
      'api.minasprojet.com.br',
    ];
    const isTrusted = trustedDomains.some(
      domain => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
    );
    const isMinasProjet =
      urlObj.hostname === 'minasprojet.com.br' || urlObj.hostname.endsWith('.minasprojet.com.br');
    const isSameOrigin = urlObj.origin === window.location.origin;

    if (!isSameOrigin && !isTrusted && !isMinasProjet) {
      console.warn('URL externa bloqueada:', url);
      return null;
    }

    // Verificar protocolo
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      console.warn('Protocolo não permitido:', urlObj.protocol);
      return null;
    }

    // Em produção, bloquear http externo (exceto localhost)
    const isLocal = ['localhost', '127.0.0.1'].includes(urlObj.hostname);
    if (urlObj.protocol === 'http:' && !isLocal) {
      console.warn('HTTP bloqueado em URL externa:', url);
      return null;
    }

    return urlObj.toString();
  } catch (error) {
    console.warn('URL inválida:', url, error);
    return null;
  }
};

/**
 * Gera nonce para CSP
 */
export const generateNonce = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Configura headers de segurança
 */
export const setSecurityHeaders = () => {
  if (typeof document === 'undefined') return;

  // Adicionar meta tag para X-Frame-Options
  const frameOptions = document.querySelector('meta[http-equiv="X-Frame-Options"]');
  if (!frameOptions) {
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', 'X-Frame-Options');
    meta.setAttribute('content', 'DENY');
    document.head.appendChild(meta);
  }

  // Adicionar meta tag para X-Content-Type-Options
  const contentTypeOptions = document.querySelector('meta[http-equiv="X-Content-Type-Options"]');
  if (!contentTypeOptions) {
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', 'X-Content-Type-Options');
    meta.setAttribute('content', 'nosniff');
    document.head.appendChild(meta);
  }

  // Adicionar meta tag para X-XSS-Protection
  const xssProtection = document.querySelector('meta[http-equiv="X-XSS-Protection"]');
  if (!xssProtection) {
    const meta = document.createElement('meta');
    meta.setAttribute('http-equiv', 'X-XSS-Protection');
    meta.setAttribute('content', '1; mode=block');
    document.head.appendChild(meta);
  }

  // Adicionar meta tag para Referrer-Policy
  const referrerPolicy = document.querySelector('meta[name="referrer"]');
  if (!referrerPolicy) {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'referrer');
    meta.setAttribute('content', 'strict-origin-when-cross-origin');
    document.head.appendChild(meta);
  }
};

/**
 * Inicializa configurações de segurança
 */
export const initializeSecurity = () => {
  // Aplicar CSP apenas em produção (evita bloquear API cross-origin no dev)
  if (!DEV) {
    applyCSP();
  }

  // Configurar headers de segurança
  setSecurityHeaders();

  // Verificar contexto seguro
  if (!isSecureContext()) {
    console.warn('Aplicação não está rodando em contexto seguro (HTTPS)');
  }

  // Log de inicialização
  console.log('Configurações de segurança inicializadas');
};

/**
 * Hook para detectar tentativas de XSS
 */
export const detectXSS = input => {
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<link/i,
    /<meta/i,
  ];

  return xssPatterns.some(pattern => pattern.test(input));
};

/**
 * Sanitiza HTML básico
 */
export const sanitizeHTML = html => {
  if (!html || typeof html !== 'string') return '';
  try {
    // Tentar DOMPurify se disponível (adicionar lib se necessário)
    if (window.DOMPurify?.sanitize) {
      return window.DOMPurify.sanitize(html, { SAFE_FOR_TEMPLATES: true });
    }
  } catch {}
  // Fallback mínimo (mesmo comportamento anterior)
  const allowedTags = ['b', 'i', 'em', 'strong', 'p', 'br'];
  let sanitized = html.replace(/<\/?[^>]+(>|$)/g, match => {
    const tagName = match.replace(/<\/?(\w+).*/, '$1').toLowerCase();
    return allowedTags.includes(tagName) ? match : '';
  });
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*javascript\s*:/gi, '');
  return sanitized;
};
